import TypeCard, {
  TypeCardImage,
  TypeCardTitle,
  TypeCardPrice,
  TypeCardBenefitList,
  TypeCardHeader,
  TypeCardIngress,
  TypeCardButton,
  ButtonAction,
} from "./TypeCard";
import { useContractTypeUpgradeAction } from "./UpgradeContractButton";
import styled from "styled-components";
import { ContractTypeBenefit } from "./ContractTypeBenefit";
import { ContractType } from "@vehicle-contract/common/lib/model/contract/SalesContract";
import SvvLogo from "../../shared/logos/SvvLogo";
import { ContractUpgradeProduct } from "../../payments/PaymentViewModel";
import BankIdLogo from "../../shared/logos/BankIdLogo";
import NokPrice from "@teamscheme/nok-price";
import ErrorMessage from "../../shared/error/ErrorMessage";
import PremiumIllustration from "./illustrations/full_91x124.svg";
import { GetContractUpgradeProductsResponse } from "./ContractTypePage";
import VippsLogo from "../../shared/logos/VippsLogo";
import { addOnClick } from "./addOnClick";
import { STANDARD_BENEFITS } from "./StandardCard";
import { getContractTypeKey } from "../create-contract/ContractTypeName";

export const PREMIUM_BENEFITS: ContractTypeBenefit[] = [
  {
    label: "Gebyrfri betaling med Vipps",
    positive: true,
  },
];

export const PremiumPackageDetails = () => (
  <>
    <p>
      Premium er kjøpekontrakten for dere som trenger en måte å sikre at
      overleveringen skjer på en trygg og rettferdig måte for begge parter.
    </p>
    <p>
      Med Premium blir eierskiftet knyttet til en betaling ved hjelp av Vipps,
      slik at de kan gjennomføres samtidig. Det betyr at kjøretøyet registreres
      på ny eier hos Statens vegvesen i samme øyeblikk som betalingen Vippses
      til selger. Kjøper kan da være sikker på at kjøretøyet er registrert på
      sitt navn, og selger kan trygt gi fra seg nøkler med betalingen trygt på
      sin konto.
    </p>
    <h3>Hvorfor velge Premium?</h3>
    <p>
      Tjenesten sikrer en rettferdig og bekymringsfri overlevering av kjøretøyet
      i det øyeblikket det er utfordrende å vite hvilken del av oppgjøret som
      skal skje først. Vi sørger for at det skjer samtidig, slik at ingen går
      hjem tomhendte. Kjøper kan vippse opptil <NokPrice value={500000} /> (
      <NokPrice value={95000} /> for DNB-kunder) med NAF Kjøpekontrakt.
      Vippsoverføringen er dessuten gebyrfri!
    </p>
  </>
);

export interface PremiumCardProps {
  product: ContractUpgradeProduct;
  className?: string;
  buttonLabel?: string;
  buttonAction: ButtonAction;
  error?: boolean;
}

const PremiumCard = styled(function (props: PremiumCardProps) {
  const { product, className, buttonLabel, buttonAction, error } = props;
  const typeKey = getContractTypeKey(product.contractType);
  return (
    <TypeCard label="Anbefalt" className={className}>
      <TypeCardHeader>
        <TypeCardImage
          src={PremiumIllustration}
          alt="Premium"
          width="91"
          height="124"
        />
        <TypeCardTitle>Premium</TypeCardTitle>
        <TypeCardIngress>
          Kontrakt inkludert eierskiftet med sikker og gebyrfri betaling av hele
          eller deler av kjøpesummen.
        </TypeCardIngress>
      </TypeCardHeader>
      <TypeCardBenefitList
        benefits={[...PREMIUM_BENEFITS, ...STANDARD_BENEFITS]}
      />
      <Logos>
        <BankIdLogoWrapper>
          <BankIdLogo />
        </BankIdLogoWrapper>
        <VippsLogoWrapper>
          <VippsLogo />
        </VippsLogoWrapper>
        <SvvLogoWrapper>
          <SvvLogo />
        </SvvLogoWrapper>
      </Logos>
      <TypeCardPrice>
        <NokPrice value={product.price} />
      </TypeCardPrice>
      <TypeCardButton typeKey={typeKey} action={buttonAction} variant="primary">
        {buttonLabel}
      </TypeCardButton>
      {error ? (
        <ErrorMessage>En feil har oppstått! Prøv igjen.</ErrorMessage>
      ) : null}
    </TypeCard>
  );
})`
  background: ${({ theme }) => theme.nafColor.signature.yellow20};
`;

export default PremiumCard;

export const PremiumUpgradeCard = styled(function ({
  upgrade,
  returnPath,
  className,
  onOpenPayment,
}: {
  upgrade: GetContractUpgradeProductsResponse;
  returnPath?: string;
  className?: string;
  onOpenPayment?: () => void;
}) {
  const client = useContractTypeUpgradeAction(upgrade, ContractType.Premium, returnPath);

  const { error, action } = client;

  const baseProps = {
    className,
    product: client.product,
    buttonLabel: "Til betaling",
    buttonAction: onOpenPayment ? addOnClick(action, onOpenPayment) : action,
    error: !!error,
  };

  return <PremiumCard {...baseProps} />;
})``;

const SvvLogoWrapper = styled.div``;
const BankIdLogoWrapper = styled.div``;
const VippsLogoWrapper = styled.div``;

const Logos = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.space16};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 0 -${({ theme }) => theme.spacing.space8} ${({ theme }) => theme.spacing.space16};

  ${SvvLogo}, ${BankIdLogo}, ${VippsLogo} {
    display: block;
    width: 100%;
    height: auto;
  }

  ${SvvLogoWrapper}, ${BankIdLogoWrapper}, ${VippsLogoWrapper} {
    flex: 1 1 33%;
    padding: 0 ${({ theme }) => theme.spacing.space8};
    max-width: ${8 * 13}px;
  }
`;
